import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import Uri from 'urijs';
import CryptoES from 'crypto-es';
import { firstValueFrom } from 'rxjs';
import { AZURE_MODULE_OPTIONS } from './azure.token';
import { AzureModuleOptions } from './azure.types';

export interface AuthResult {
  access_token: string;
  expires_on: number;
  refresh_token: string;
  refresh_token_expires_in: number;
  id_token: string;
}

@Injectable({
  providedIn: 'root',
})
export class AzureAuthService {
  private baseBody: HttpParams;
  private readonly redirectUri: string;
  private readonly postLogoutRedirectUri: string;

  constructor(
    private httpClient: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    @Inject(AZURE_MODULE_OPTIONS) private readonly options: AzureModuleOptions,
  ) {
    this.redirectUri = `${document.location.origin}${options.redirectUri}`;
    this.postLogoutRedirectUri = `${document.location.origin}/auth`;

    this.baseBody = new HttpParams()
      .set('client_id', options.clientId)
      .set('redirect_uri', this.redirectUri);
  }

  async logIn(state: string) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.document.location.href = (await this.getAuthUrlForApp(state))!;
  }

  logOut() {
    this.document.location.href = new Uri(this.options.logoutUri)
      .addQuery({
        post_logout_redirect_uri: this.postLogoutRedirectUri,
      })
      .toString();
  }

  async handleAuthCodeFlow(
    redirectState: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any,
  ): Promise<AuthResult> {
    if (!params.state && redirectState !== params.state) {
      throw new Error(
        `CSRF suspected! state before redirect: ${redirectState} | after redirect: ${params.state}`,
      );
    }

    if (params.code) {
      // here we have the callback handling for getting the initial refresh token with the auth code flow
      // TODO: remove localStorage access
      const pkceVerifier = window.localStorage.getItem('pkceVerifier');
      window.localStorage.removeItem('pkceVerifier');

      const body = this.baseBody
        .set('grant_type', 'authorization_code')
        .set('code', params.code)
        .set('scope', this.options.scope)
        .set('code_verifier', pkceVerifier as string);

      return firstValueFrom(
        this.httpClient.post<AuthResult>(
          this.options.tokenUri,
          body.toString(),
          {
            headers: new HttpHeaders().set(
              'Content-Type',
              'application/x-www-form-urlencoded',
            ),
          },
        ),
      );
    } else {
      throw new Error(`Auth code (${params.code || 'NA'}) is not available`);
    }
  }

  async acquireTokenSilent(refreshToken: string): Promise<AuthResult> {
    const body = this.baseBody
      .set('scope', this.options.scope)
      .set('grant_type', 'refresh_token')
      .set('refresh_token', refreshToken);

    return firstValueFrom(
      this.httpClient.post<AuthResult>(this.options.tokenUri, body.toString(), {
        headers: new HttpHeaders().set(
          'Content-Type',
          'application/x-www-form-urlencoded',
        ),
      }),
    );
  }

  // TODO: use this function to silently get api specific tokens
  //  this requires to manage multiple tokens in the app
  //  do NOT join the scopes in the "app.config.ts" when using this approach with the on behalf of flow
  private async getAuthUrlForApi(appToken: string, scope: string) {
    return new Uri(this.options.tokenUri).addQuery({
      grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
      client_id: this.options.clientId,
      client_secret: 'client_secret_from_1password',
      assertion: appToken,
      scope,
      requested_token_use: 'on_behalf_of',
    }) as unknown as string;
  }

  private async getAuthUrlForApp(state: string) {
    const pkceVerifier = this.generateRandomString();
    const pkceCodeChallenge =
      await this.pkceChallengeFromVerifier(pkceVerifier);
    // TODO: remove localStorage access
    window.localStorage.setItem('pkceVerifier', pkceVerifier);

    return new Uri(this.options.authorizeUri).addQuery({
      client_id: this.options.clientId,
      response_type: 'code',
      redirect_uri: this.redirectUri,
      response_mode: 'query',
      state,
      code_challenge: pkceCodeChallenge,
      code_challenge_method: 'S256',
      scope: this.options.scope,
    }) as unknown as string;
  }

  // PKCE code challenge as implemented in https://github.com/aaronpk/pkce-vanilla-js/blob/master/index.html
  private generateRandomString() {
    // TODO: min length 43, max length 128 => 128 produces errors (too long?); check if 60 is ok
    const array = new Uint32Array(60);
    window.crypto.getRandomValues(array);
    return Array.from(array, (dec) => ('0' + dec.toString(16)).substr(-2)).join(
      '',
    );
  }

  // Return the base64-urlencoded sha256 hash for the PKCE challenge
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async pkceChallengeFromVerifier(verifier: any) {
    return CryptoES.SHA256(verifier)
      .toString(CryptoES.enc.Base64)
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }
}
